import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { dncAPI } from '../../services/api';
import {
  PhoneOff, Plus, Upload, Download, Search, Trash2,
  AlertCircle, CheckCircle, Loader2, X, FileText, History,
  User, Clock, Phone, Filter
} from 'lucide-react';

const DNCListPage = () => {
  const { user } = useAuth();
  const [dncList, setDncList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newReason, setNewReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'quick_add'
  const [quickAddCount, setQuickAddCount] = useState(0);

  const fetchDNCList = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (search) params.search = search;
      if (activeTab === 'quick_add') params.quick_add_only = 'true';
      
      const response = await dncAPI.getList(user?.id, params);
      
      if (response.data.success) {
        setDncList(response.data.numbers || []);
        setTotal(response.data.total || 0);
        if (response.data.quick_add_count !== undefined) {
          setQuickAddCount(response.data.quick_add_count);
        }
      }
    } catch (error) {
      console.error('Error fetching DNC list:', error);
      setMessage({ type: 'error', text: 'Failed to load DNC list' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, search, activeTab]);

  useEffect(() => {
    if (user?.id) {
      fetchDNCList();
    }
  }, [user?.id, fetchDNCList]);

  const handleAddNumber = async (e) => {
    e.preventDefault();
    if (!newNumber.trim()) return;
    
    setAdding(true);
    try {
      const response = await dncAPI.addNumber(user?.id, newNumber, newReason || 'Manual entry');
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Number added to DNC list' });
        setNewNumber('');
        setNewReason('');
        setShowAddModal(false);
        fetchDNCList();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add number';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      const response = await dncAPI.removeNumber(user?.id, id);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Number removed from DNC list' });
        fetchDNCList();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove number' });
    } finally {
      setDeleting(null);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await dncAPI.bulkImport(user?.id, formData);
      
      if (response.data.success) {
        setImportResult(response.data);
        fetchDNCList();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Import failed';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await dncAPI.exportList(user?.id);
      
      // Handle blob response
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dnc_list_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: 'Export failed' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] p-6" data-testid="dnc-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-red-500/20 rounded-xl">
            <PhoneOff className="w-7 h-7 text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Do Not Call List</h1>
            <p className="text-gray-400 text-sm">
              Manage phone numbers that should never be called
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
            data-testid="export-dnc-btn"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors"
            data-testid="import-dnc-btn"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors"
            data-testid="add-dnc-btn"
          >
            <Plus className="w-4 h-4" />
            Add Number
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
        }`}>
          {message.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1a1a1f] border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Total Blocked Numbers</p>
          <p className="text-2xl font-bold text-white">{total.toLocaleString()}</p>
        </div>
        <div className="bg-[#1a1a1f] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <History className="w-4 h-4 text-orange-400" />
            Quick Add History
          </div>
          <p className="text-2xl font-bold text-orange-400">{quickAddCount.toLocaleString()}</p>
        </div>
        <div className="bg-[#1a1a1f] border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Added This Month</p>
          <p className="text-2xl font-bold text-white">
            {dncList.filter(n => {
              const date = new Date(n.created_at);
              const now = new Date();
              return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
            }).length}
          </p>
        </div>
        <div className="bg-[#1a1a1f] border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Calls Blocked (Est.)</p>
          <p className="text-2xl font-bold text-teal-400">{(total * 3).toLocaleString()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <PhoneOff className="w-4 h-4" />
          All Numbers
          <span className="px-2 py-0.5 bg-gray-700 rounded-full text-xs">{total}</span>
        </button>
        <button
          onClick={() => setActiveTab('quick_add')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'quick_add'
              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <History className="w-4 h-4" />
          Quick Add History
          <span className="px-2 py-0.5 bg-gray-700 rounded-full text-xs">{quickAddCount}</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-[#1a1a1f] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder={activeTab === 'quick_add' ? "Search by phone, reason, or agent name..." : "Search by phone number or reason..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#0d1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-500"
            data-testid="dnc-search-input"
          />
        </div>
      </div>

      {/* DNC List Table */}
      <div className="bg-[#1a1a1f] border border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          </div>
        ) : dncList.length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'quick_add' ? (
              <>
                <History className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No Quick Add history</p>
                <p className="text-gray-500 text-sm mt-1">Numbers added by agents during calls will appear here</p>
              </>
            ) : (
              <>
                <PhoneOff className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No numbers in DNC list</p>
                <p className="text-gray-500 text-sm mt-1">Add numbers to prevent them from being called</p>
              </>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Phone Number</th>
                <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Reason</th>
                {activeTab === 'quick_add' && (
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Added By</th>
                )}
                <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Added On</th>
                <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dncList.map((item) => (
                <tr key={item.id} className="border-b border-gray-800/50 hover:bg-[#252530]">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {item.added_by_user_id && (
                        <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded" title="Quick Add">
                          QA
                        </span>
                      )}
                      <span className="text-white font-mono">{item.phone_display || item.phone_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300 text-sm">{item.reason || 'N/A'}</span>
                    {item.notes && (
                      <p className="text-gray-500 text-xs mt-0.5 truncate max-w-[200px]" title={item.notes}>
                        {item.notes}
                      </p>
                    )}
                  </td>
                  {activeTab === 'quick_add' && (
                    <td className="px-6 py-4">
                      {item.added_by_user_name ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                            <User className="w-3 h-3 text-orange-400" />
                          </div>
                          <span className="text-gray-300 text-sm">{item.added_by_user_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-gray-400 text-sm">
                      <Clock className="w-3 h-3" />
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                    <p className="text-gray-500 text-xs">
                      {new Date(item.created_at).toLocaleTimeString()}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDelete(item.id)}
                      disabled={deleting === item.id}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      data-testid={`delete-dnc-${item.id}`}
                    >
                      {deleting === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Number Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1f] border border-gray-800 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Add to DNC List</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddNumber}>
              <div className="mb-4">
                <label className="block text-gray-400 text-sm mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={newNumber}
                  onChange={(e) => setNewNumber(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-3 bg-[#0d1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                  required
                  data-testid="new-dnc-number-input"
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Reason (optional)</label>
                <input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="e.g., Customer requested, Complaint"
                  className="w-full px-4 py-3 bg-[#0d1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                  data-testid="new-dnc-reason-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="flex-1 px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center justify-center gap-2"
                  data-testid="confirm-add-dnc-btn"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add Number
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1f] border border-gray-800 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Import DNC Numbers</h3>
              <button onClick={() => { setShowImportModal(false); setImportResult(null); }} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {importResult ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-white font-medium mb-2">Import Complete</p>
                <div className="bg-[#0d1117] rounded-lg p-4 text-left">
                  <p className="text-gray-300"><span className="text-green-400">{importResult.added}</span> numbers added</p>
                  <p className="text-gray-300"><span className="text-yellow-400">{importResult.skipped}</span> duplicates skipped</p>
                  {importResult.invalid > 0 && (
                    <p className="text-gray-300"><span className="text-red-400">{importResult.invalid}</span> invalid numbers</p>
                  )}
                </div>
                <button
                  onClick={() => { setShowImportModal(false); setImportResult(null); }}
                  className="mt-4 px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-gray-400 text-sm mb-3">
                    Upload a CSV or TXT file with phone numbers. One number per line or comma-separated.
                  </p>
                  <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center">
                    <FileText className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                    <input
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleImportFile}
                      className="hidden"
                      id="dnc-file-input"
                      data-testid="dnc-file-input"
                    />
                    <label
                      htmlFor="dnc-file-input"
                      className="cursor-pointer text-teal-400 hover:text-teal-300"
                    >
                      {importing ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Importing...
                        </span>
                      ) : (
                        'Click to select file'
                      )}
                    </label>
                    <p className="text-gray-500 text-xs mt-2">CSV or TXT, max 10MB</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="w-full px-4 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DNCListPage;
